import { useEffect, useState } from "preact/hooks";

import { api } from "@/api/client";
import {
  type InsightFigures,
  type InsightListResponse,
  type InsightReport,
  type InsightSummary,
  type ModeInsight,
  PLACE_ROLES,
} from "@/api/types";
import { CountTable } from "@/components/CountTable";
import { ExcerptRefs } from "@/components/ExcerptRefs";
import { FeedbackCounts } from "@/components/FeedbackCounts";
import { InterestTerms } from "@/components/InterestTerms";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { type PollResult, usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { addDays, dayLabel, formatDay, zurichToday } from "@/lib/day";
import {
  formatDateTime,
  formatDurationSeconds,
  formatInt,
  formatUsdFine,
  modeLabel,
} from "@/lib/format";
import { creatorLabel, failureText, insightError, REPORT_RETENTION_DAYS } from "@/lib/insights";
import { renderAnalysisMarkdown } from "@/lib/renderMarkdown";
import { affectLabel, intentLabel, placeRoleLabel } from "@/lib/vocabulary";
import {
  type InsightRun,
  insightRun,
  insightsVersion,
  isInsightRunning,
  runInsightReport,
  selectedInsightDay,
} from "@/state/insights";

// «Gespräche › Tagesbericht»: one Zurich day per report, its figures per mode
// counted by code, and the analysis-chat model's reading of them. The nightly
// job reports the day before at 03:00; a run by hand builds any day of the
// retention window and replaces that day's report. The run lives in
// `state/insights.ts`, so it outlasts the view. None of the header's filters
// apply: a report covers every partner and never a test session.

export function InsightView() {
  const version = insightsVersion.value;
  const list = usePoll((s) => api.insights(s), [version]);
  const reports = list.data?.reports ?? [];
  const day = selectedInsightDay.value || reports[0]?.day || "";
  return (
    <div class="insight-view">
      <RunCard reports={reports} />
      <ListCard list={list} shown={day} />
      {day && <ReportCard key={day} day={day} version={version} />}
    </div>
  );
}

// --- Bericht erstellen -----------------------------------------------------------

function RunCard({ reports }: { reports: readonly InsightSummary[] }) {
  const run = insightRun.value;
  const running = isInsightRunning(run);
  const today = zurichToday();
  const earliest = addDays(today, -REPORT_RETENTION_DAYS);
  const [day, setDay] = useState(() => addDays(today, -1));
  // An emptied field is no day either.
  const inRange = day >= earliest && day <= today;
  const replaces = reports.some((report) => report.day === day);

  const submit = (e: Event) => {
    e.preventDefault();
    if (inRange && !running) void runInsightReport(day);
  };

  return (
    <section class="card" aria-label={t("insights.title")}>
      <h2>{t("insights.title")}</h2>
      <PanelHelp k="insights.overview" />
      <h3>{t("insights.run.title")}</h3>
      <PanelHelp k="insights.run" />
      <form class="qc-actions" onSubmit={submit}>
        <label class="toggle">
          <span>{t("insights.run.day")}</span>
          <input
            type="date"
            class="ops-input"
            min={earliest}
            max={today}
            value={day}
            disabled={running}
            onInput={(e) => setDay(e.currentTarget.value)}
          />
        </label>
        <button type="submit" class="window-pill active" disabled={running || !inRange}>
          {t("insights.run.submit")}
        </button>
      </form>
      {!inRange && (
        <div class="limit-warn">
          {t("insights.run.outOfRange", { from: formatDay(earliest), to: formatDay(today) })}
        </div>
      )}
      {inRange && day === today && <div class="kpi-sub">{t("insights.run.today")}</div>}
      {inRange && replaces && (
        <div class="kpi-sub">{t("insights.run.replaces", { day: formatDay(day) })}</div>
      )}
      {running && <RunProgress run={run} />}
      {run.error && (
        <div class="error-banner" role="alert">
          {run.error}
        </div>
      )}
    </section>
  );
}

/** The time now, renewed every `intervalMs`. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

// One model call has no steps to count: the bar says that it runs, the lines
// what and for how long. Only the first is announced; the ticking time is not.
function RunProgress({ run }: { run: InsightRun }) {
  const now = useNow(1_000);
  const elapsed = t("insights.run.elapsed", {
    elapsed: formatDurationSeconds(Math.max(0, (now - run.startedAt) / 1000)),
  });
  return (
    <div class="insight-progress">
      <div
        class="funnel-track"
        role="progressbar"
        aria-label={t("insights.run.title")}
        aria-valuetext={elapsed}
      >
        <div class="funnel-fill insight-progress-fill" />
      </div>
      <div class="kpi-sub" role="status">
        {t("insights.run.busy", { day: formatDay(run.day) })}
      </div>
      <div class="kpi-sub">{elapsed}</div>
    </div>
  );
}

// --- Gespeicherte Berichte ------------------------------------------------------

function ListCard({ list, shown }: { list: PollResult<InsightListResponse>; shown: string }) {
  const reports = list.data?.reports ?? [];
  return (
    <section class="card" aria-label={t("insights.list.title")}>
      <div class="qc-head">
        <h2>{t("insights.list.title")}</h2>
        <Staleness lastFetched={list.lastFetched} loading={list.loading} onRefresh={list.refetch} />
      </div>
      <PanelHelp k="insights.list" />
      {list.error && <div class="error-banner">{insightError(list.error)}</div>}
      {list.data && reports.length === 0 && (
        <div class="kpi-sub">{t("insights.list.none")}</div>
      )}
      {reports.length > 0 && (
        <div class="qc-scroll">
          <table class="table" aria-label={t("insights.list.title")}>
            <thead>
              <tr>
                <th>{t("insights.list.col.day")}</th>
                <th>{t("insights.list.col.conversations")}</th>
                <th>{t("insights.list.col.questions")}</th>
                <th>{t("insights.list.col.narrative")}</th>
                <th>{t("insights.list.col.created")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => {
                const current = report.day === shown;
                return (
                  <tr key={report.day} class={current ? "row-selected" : undefined}>
                    <td>
                      <button
                        type="button"
                        class="qc-disclosure"
                        aria-pressed={current}
                        onClick={() => (selectedInsightDay.value = report.day)}
                      >
                        {dayLabel(report.day)}
                      </button>
                      {report.partial && <span class="insight-badge">{t("insights.partial")}</span>}
                    </td>
                    <td>{formatInt(report.conversations)}</td>
                    <td>{formatInt(report.questions)}</td>
                    <td>
                      {t(report.has_narrative ? "insights.list.withText" : "insights.list.figuresOnly")}
                    </td>
                    <td>
                      {t("insights.list.created", {
                        when: formatDateTime(report.created_at),
                        by: creatorLabel(report.created_by),
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- Der Bericht ----------------------------------------------------------------

// One card from loading to loaded, so what points at it stays valid.
function ReportCard({ day, version }: { day: string; version: number }) {
  const { data, error } = usePoll((s) => api.insight(day, s), [day, version]);
  const title = t("insights.report.title", { day: dayLabel(day) });
  return (
    <section class="card" aria-label={title}>
      <h2>
        {title}
        {data?.partial && <span class="insight-badge">{t("insights.partial")}</span>}
      </h2>
      {data ? (
        <Report report={data} />
      ) : error ? (
        <div class="error-banner" role="alert">
          {insightError(error)}
        </div>
      ) : (
        <div class="kpi-sub">{t("common.loading")}</div>
      )}
    </section>
  );
}

function Report({ report }: { report: InsightReport }) {
  const { figures } = report;
  // A report made while its day ran compares with nothing.
  const baselineHead =
    report.baseline_days > 0
      ? t("insights.terms.baseline", { days: formatInt(report.baseline_days) })
      : null;
  return (
    <>
      <div class="kpi-sub">
        {t("insights.report.meta", {
          when: formatDateTime(report.created_at),
          by: creatorLabel(report.created_by),
        })}
      </div>
      <div class="kpi-sub">
        {baselineHead === null
          ? t("insights.report.noCompare")
          : t("insights.report.compare", { days: formatInt(report.baseline_days) })}
      </div>
      {!figures.complete && <div class="limit-warn">{t("insights.report.incomplete")}</div>}

      <h3>{t("insights.narrative.title")}</h3>
      <PanelHelp k="insights.narrative" />
      <Narrative report={report} />

      <h3>{t("insights.day.title")}</h3>
      <PanelHelp k="insights.figures" />
      <DayFigures figures={figures} />

      <h3>{t("insights.terms.title")}</h3>
      <PanelHelp k="insights.terms" />
      <InterestTerms
        title={t("insights.terms.title")}
        rows={figures.terms}
        baselineHead={baselineHead}
        empty={t("insights.terms.none")}
      />

      {figures.modes.length > 0 && (
        <>
          <h3>{t("insights.modeDetail.title")}</h3>
          <PanelHelp k="insights.modeDetail" />
          {figures.modes.map((mode) => (
            <ModeDetail key={mode.mode} mode={mode} baselineHead={baselineHead} />
          ))}
        </>
      )}
    </>
  );
}

function Narrative({ report }: { report: InsightReport }) {
  return (
    <>
      {report.narrative ? (
        // Sanitised by renderAnalysisMarkdown (DOMPurify), like the Analyse-Chat's answers.
        <div
          class="rendered-answer insight-narrative"
          dangerouslySetInnerHTML={{ __html: renderAnalysisMarkdown(report.narrative) }}
        />
      ) : (
        <div class={report.failure === "no_conversations" ? "kpi-sub" : "limit-warn"}>
          {failureText(report.failure)}
        </div>
      )}
      {report.narrative_model && (
        <div class="kpi-sub">
          {t("insights.narrative.meta", {
            model: report.narrative_model,
            cost: formatUsdFine(report.cost_usd),
          })}
        </div>
      )}
      <ExcerptRefs refs={report.excerpt_refs} />
    </>
  );
}

function Total({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <div class="kpi">{formatInt(value)}</div>
      <div class="kpi-sub">{label}</div>
    </div>
  );
}

function DayFigures({ figures }: { figures: InsightFigures }) {
  return (
    <>
      <div class="insight-totals">
        <Total value={figures.conversations} label={t("insights.totals.conversations")} />
        <Total value={figures.questions} label={t("insights.totals.questions")} />
        <Total value={figures.no_answer_events} label={t("insights.totals.noAnswer")} />
      </div>
      {figures.modes.length > 0 && (
        <div class="qc-scroll">
          <table class="table" aria-label={t("insights.modes.title")}>
            <thead>
              <tr>
                <th>{t("insights.col.mode")}</th>
                <th>{t("insights.col.conversations")}</th>
                <th>{t("insights.col.questions")}</th>
                <th>{t("insights.col.replies")}</th>
                <th>{t("insights.col.ratings")}</th>
                <th>{t("insights.col.oneQuestion")}</th>
                <th>{t("insights.col.noPage")}</th>
                <th>{t("insights.col.weakMatch")}</th>
              </tr>
            </thead>
            <tbody>
              {figures.modes.map((mode) => (
                <tr key={mode.mode}>
                  <td>{modeLabel(mode.mode)}</td>
                  <td>{formatInt(mode.conversations)}</td>
                  <td>{formatInt(mode.questions)}</td>
                  <td>{formatInt(mode.replies)}</td>
                  <td>
                    <FeedbackCounts
                      positive={mode.feedback_positive}
                      negative={mode.feedback_negative}
                    />
                  </td>
                  <td>{formatInt(mode.one_question_conversations)}</td>
                  <td>{formatInt(mode.no_page)}</td>
                  <td>{formatInt(mode.weak_match)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ModeDetail({ mode, baselineHead }: { mode: ModeInsight; baselineHead: string | null }) {
  const label = modeLabel(mode.mode);
  const none = t("insights.none");
  return (
    <section class="insight-mode" aria-label={label}>
      <h4>{label}</h4>
      <div class="insight-lists">
        <CountTable
          title={t("engagement.chart.intent")}
          counts={mode.intents}
          label={intentLabel}
          unit={t("insights.unit.questions")}
          empty={none}
        />
        <CountTable
          title={t("engagement.chart.sessionIntent")}
          counts={mode.conversation_intents}
          label={intentLabel}
          unit={t("insights.unit.conversations")}
          empty={none}
        />
        <CountTable
          title={t("engagement.chart.berufsfeld")}
          counts={mode.berufsfelder}
          unit={t("insights.unit.messages")}
          empty={none}
        />
        {PLACE_ROLES.map((role) => (
          <CountTable
            key={role}
            title={placeRoleLabel(role)}
            counts={mode.places[role] ?? {}}
            unit={t("insights.unit.conversations")}
            empty={none}
          />
        ))}
        <CountTable
          title={t("engagement.chart.affect")}
          counts={mode.affect}
          label={affectLabel}
          unit={t("insights.unit.messages")}
          empty={none}
        />
      </div>
      <InterestTerms
        title={t("insights.terms.title")}
        rows={mode.terms}
        baselineHead={baselineHead}
        empty={t("insights.terms.none")}
      />
    </section>
  );
}
